import { Modal } from "@mantine/core"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { Button } from "@shopify/polaris"
import { useUpdateSubscription } from "../../hooks/usePaddleHooks.js"

export const ChangeBillingInterval = ({ activeSubscription }) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const updateSubscription = useUpdateSubscription()

	const [
		loadingChangeSubscriptionIntervalToYearly,
		setLoadingChangeSubscriptionIntervalToYearly,
	] = useState(false)
	const [showChangeBillingIntervalModal, setShowChangeBillingIntervalModal] =
		useState(false)
	const [error, setError] = useState(null)

	const [currentTotal, setCurrentTotal] = useState(0)
	const [newTotal, setNewTotal] = useState(0)

	const changeSubscriptionBillingIntervalToYearly = async () => {
		setError(null)
		const activeSubscriptionId =
			state.kopyBasicSubscription?.subscriptionId ||
			state.kopyProtectSubscription?.subscriptionId ||
			null
		const newPriceIds = []

		const userHasKopyBasicDiscountedSubscription =
			state.kopyBasicSubscription?.priceId ===
				state.pricing?.["kopy_basic"]?.["monthly"]?.["discounted"]?.[
					"no_trial"
				]?.priceId ||
			state.kopyBasicSubscription?.priceId ===
				state.pricing?.["kopy_basic"]?.["monthly"]?.["discounted"]?.[
					"with_trial"
				]?.priceId

		const userHasKopyBasicTrialSubscription =
			state.kopyBasicSubscription?.priceId ===
				state.pricing?.["kopy_basic"]?.["monthly"]?.["basic"]?.[
					"with_trial"
				]?.priceId ||
			state.kopyBasicSubscription?.priceId ===
				state.pricing?.["kopy_basic"]?.["monthly"]?.["discounted"]?.[
					"with_trial"
				]?.priceId

		if (state.hasValidKopyBasicSubscription) {
			newPriceIds.push(
				state.pricing?.["kopy_basic"]?.["yearly"]?.[
					userHasKopyBasicDiscountedSubscription
						? "discounted"
						: "base"
				]?.[
					userHasKopyBasicTrialSubscription
						? "with_trial"
						: "no_trial"
				]?.priceId || null,
			)
		}

		if (state.hasValidKopyPlusSubscription) {
			const userHasKopyPlusTrialSubscription =
				state.kopyPlusSubscription?.priceId ===
				state.pricing?.["kopy_plus"]?.["monthly"]?.["with_trial"]
					?.priceId

			newPriceIds.push(
				state.pricing?.["kopy_plus"]?.["yearly"]?.[
					userHasKopyPlusTrialSubscription ? "with_trial" : "no_trial"
				]?.priceId || null,
			)
		}

		if (state.hasValidKopyProtectSubscription) {
			const userHasKopyProtectTrialSubscription =
				state.kopyProtectSubscription?.priceId ===
				state.pricing?.["kopy_protect"]?.["monthly"]?.["with_trial"]
					?.priceId

			newPriceIds.push(
				state.pricing?.["kopy_protect"]?.["yearly"]?.[
					userHasKopyProtectTrialSubscription
						? "with_trial"
						: "no_trial"
				]?.priceId || null,
			)
		}

		if (!newPriceIds.length) {
			setError("Price Id not found")
		}

		if (!activeSubscriptionId) {
			setError("Subscription Id not found")
		}

		setLoadingChangeSubscriptionIntervalToYearly(true)
		const { success } = await updateSubscription({
			items: newPriceIds.map((priceId) => ({
				price_id: priceId,
				quantity: 1,
			})),
			prorationBillingMode: "prorated_immediately",
			subscriptionId: activeSubscriptionId,
		})
		setLoadingChangeSubscriptionIntervalToYearly(false)

		if (success) {
			window.location.reload()
		} else {
			setError("Failed to change subscription")
		}
	}

	useEffect(() => {
		if (state.pricing) {
			const currentTotal =
				(state.kopyBasicSubscription?.priceInUsd || 0) +
				(state.kopyPlusSubscription?.priceInUsd || 0) +
				(state.kopyProtectSubscription?.priceInUsd || 0)
			setCurrentTotal(currentTotal)

			const userHasKopyBasicDiscountedSubscription =
				state.kopyBasicSubscription?.priceId ===
					state.pricing?.["kopy_basic"]?.["monthly"]?.[
						"discounted"
					]?.["no_trial"]?.priceId ||
				state.kopyBasicSubscription?.priceId ===
					state.pricing?.["kopy_basic"]?.["monthly"]?.[
						"discounted"
					]?.["with_trial"]?.priceId

			const userHasKopyBasicTrialSubscription =
				state.kopyBasicSubscription?.priceId ===
					state.pricing?.["kopy_basic"]?.["monthly"]?.["basic"]?.[
						"with_trial"
					]?.priceId ||
				state.kopyBasicSubscription?.priceId ===
					state.pricing?.["kopy_basic"]?.["monthly"]?.[
						"discounted"
					]?.["with_trial"]?.priceId

			const kopyBasicTotal = state.hasValidKopyBasicSubscription
				? state.pricing?.["kopy_basic"]?.["yearly"]?.[
						userHasKopyBasicDiscountedSubscription
							? "discounted"
							: "base"
					]?.[
						userHasKopyBasicTrialSubscription
							? "with_trial"
							: "no_trial"
					]?.priceInUsd || 0
				: 0

			const userHasKopyPlusTrialSubscription =
				state.kopyPlusSubscription?.priceId ===
				state.pricing?.["kopy_plus"]?.["monthly"]?.["with_trial"]
					?.priceId

			const kopyPlusTotal = state.hasValidKopyPlusSubscription
				? state.pricing?.["kopy_plus"]?.["yearly"]?.[
						userHasKopyPlusTrialSubscription
							? "with_trial"
							: "no_trial"
					]?.priceInUsd || 0
				: 0

			const userHasKopyProtectTrialSubscription =
				state.kopyProtectSubscription?.priceId ===
				state.pricing?.["kopy_protect"]?.["monthly"]?.["with_trial"]
					?.priceId

			const kopyProtectTotal = state.hasValidKopyProtectSubscription
				? state.pricing?.["kopy_protect"]?.["yearly"]?.[
						userHasKopyProtectTrialSubscription
							? "with_trial"
							: "no_trial"
					]?.priceInUsd || 0
				: 0

			const newTotal = kopyBasicTotal + kopyPlusTotal + kopyProtectTotal
			setNewTotal(newTotal)
		}
	}, [])

	const canChangeFromMonthlyToYearly =
		activeSubscription.billingInterval === "monthly"

	return (
		<div>
			<Button
				fullWidth
				disabled={!canChangeFromMonthlyToYearly}
				onClick={() => setShowChangeBillingIntervalModal(true)}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "0.3rem",
						alignItems: "center",
						paddingBottom: "0.3rem",
					}}
				>
					<div>
						<svg
							style={{
								height: "1.7rem",
								width: "1.7rem",
								color: "#333",
								margin: "0.15rem",
							}}
							viewBox="0 0 24 24"
						>
							<g
								fill="none"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
							>
								<path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5M16 2v4M8 2v4m-5 4h5m9.5 7.5L16 16.3V14"></path>
								<circle cx="16" cy="16" r="6"></circle>
							</g>
						</svg>
					</div>
					<div
						style={{
							display: "flex",
							gap: "0.3rem",
							alignItems: "center",
						}}
					>
						<span style={{ fontSize: "0.85rem" }}>
							{t("account.change_subscription_to")}{" "}
							{activeSubscription.billingInterval === "monthly"
								? t("account.yearly_billing")
								: t("account.monthly_billing")}
						</span>
					</div>
				</div>
				{/* <svg xmlns="http://www.w3.org/2000/svg" width="1rem" height="1rem" viewBox="0 0 24 24"><path fill="currentColor" d="M16.15 13H5q-.425 0-.713-.288T4 12q0-.425.288-.713T5 11h11.15L13.3 8.15q-.3-.3-.288-.7t.288-.7q.3-.3.713-.313t.712.288L19.3 11.3q.15.15.213.325t.062.375q0 .2-.063.375t-.212.325l-4.575 4.575q-.3.3-.712.288t-.713-.313q-.275-.3-.288-.7t.288-.7L16.15 13Z"></path></svg> */}
			</Button>

			<Modal
				title={
					<div style={{ fontSize: "1.3rem", fontWeight: "600" }}>
						{t("account.confirm_changes")}
					</div>
				}
				centered
				radius="md"
				opened={showChangeBillingIntervalModal}
				onClose={() => setShowChangeBillingIntervalModal(false)}
			>
				<div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
					{t(
						"account.do_you_wish_to_change_your_billing_interval_from",
					)}{" "}
					<b>{t("account.monthly_billing")}</b> {t("common.to")}{" "}
					<b>{t("account.yearly_billing")}</b>?
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "center",
						marginBottom: "2rem",
						justifyContent: "center",
						gap: "1rem",
					}}
				>
					<div
						style={{
							padding: "0.2rem 0.5rem",
							background: "#eee",
							borderRadius: "0.5rem",
						}}
					>
						{new Intl.NumberFormat("en-US", {
							style: "currency",
							currency: "USD",
						}).format(currentTotal)}{" "}
						{t("account.month")}
					</div>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1.3rem"
						height="1.3rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M16.15 13H5q-.425 0-.713-.288T4 12q0-.425.288-.713T5 11h11.15L13.3 8.15q-.3-.3-.288-.7t.288-.7q.3-.3.713-.313t.712.288L19.3 11.3q.15.15.213.325t.062.375q0 .2-.063.375t-.212.325l-4.575 4.575q-.3.3-.712.288t-.713-.313q-.275-.3-.288-.7t.288-.7L16.15 13Z"
						></path>
					</svg>
					<div
						style={{
							padding: "0.2rem 0.5rem",
							background: "#eee",
							borderRadius: "0.5rem",
						}}
					>
						{new Intl.NumberFormat("en-US", {
							style: "currency",
							currency: "USD",
						}).format(newTotal)}{" "}
						{t("account.year")}
					</div>
				</div>

				<Button
					fullWidth
					styles={(theme) => ({
						root: {
							backgroundColor: "#5371FF",
							"&:hover": {
								backgroundColor: "#5371FF",
							},
						},
					})}
					radius="md"
					size="md"
					onClick={() => changeSubscriptionBillingIntervalToYearly()}
				>
					{loadingChangeSubscriptionIntervalToYearly ? (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="1rem"
							height="1rem"
							viewBox="0 0 24 24"
						>
							<g>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".14"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".29"
									transform="rotate(30 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".43"
									transform="rotate(60 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".57"
									transform="rotate(90 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".71"
									transform="rotate(120 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".86"
									transform="rotate(150 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									transform="rotate(180 12 12)"
								></rect>
								<animateTransform
									attributeName="transform"
									calcMode="discrete"
									dur="0.75s"
									repeatCount="indefinite"
									type="rotate"
									values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
								></animateTransform>
							</g>
						</svg>
					) : (
						t("common.confirm")
					)}
				</Button>
			</Modal>
		</div>
	)
}
